import React from 'react';

/**
 * Reusable Modal component for alerts and confirmations
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Modal message content
 * @param {function} onConfirm - Callback for confirm button
 * @param {function} onCancel - Callback for cancel button (optional)
 * @param {string} confirmText - Text for confirm button (default: "OK")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {boolean} showCancel - Whether to show cancel button (default: false for alerts)
 * @param {React.ReactNode} children - Custom content to render instead of message (optional)
 */
const Modal = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "OK",
  cancelText = "Cancel",
  showCancel = false,
  children
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.2s ease-in'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '4px solid #FFDC00',
        borderRadius: '0',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 0 30px rgba(255, 220, 0, 0.5)',
        animation: 'scaleIn 0.3s ease-out'
      }}>
        {title && (
          <h2 style={{
            fontFamily: "'Bangers', 'Arial Black', sans-serif",
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
            color: '#FFDC00',
            textAlign: 'center',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '2px 2px 0px #000, 3px 3px 0px #FF0000'
          }}>
            {title}
          </h2>
        )}

        {children ? (
          <div style={{
            fontFamily: "'Helvetica', 'Arial', sans-serif",
            fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
            fontWeight: 'normal',
            color: '#FFFFFF',
            textAlign: 'center',
            marginBottom: '25px',
            lineHeight: '1.5'
          }}>
            {children}
          </div>
        ) : (
          <p style={{
            fontFamily: "'Helvetica', 'Arial', sans-serif",
            fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
            fontWeight: 'normal',
            color: '#FFFFFF',
            textAlign: 'center',
            marginBottom: '25px',
            lineHeight: '1.5'
          }}>
            {message}
          </p>
        )}

        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {showCancel && (
            <button
              onClick={handleCancel}
              style={{
                padding: '12px 30px',
                fontSize: 'clamp(1rem, 2vw, 1.3rem)',
                fontWeight: '900',
                fontFamily: "'Bangers', 'Arial Black', sans-serif",
                color: '#FFFFFF',
                background: 'linear-gradient(135deg, #00CC00 0%, #009900 100%)',
                border: 'none',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 6px 12px rgba(0, 0, 0, 0.8)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)';
              }}
            >
              {cancelText}
            </button>
          )}

          <button
            onClick={handleConfirm}
            style={{
              padding: '12px 30px',
              fontSize: 'clamp(1rem, 2vw, 1.3rem)',
              fontWeight: '900',
              fontFamily: "'Bangers', 'Arial Black', sans-serif",
              color: '#FFFFFF',
              background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 6px 12px rgba(0, 0, 0, 0.8)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 0 0 2px #FFDC00, 0 0 0 4px #000, 0 4px 8px rgba(0, 0, 0, 0.6)';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Modal;
